import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles, categories } from '@/mocks/homeData';

const webpageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'j9平台资讯 - 全部文章',
  description: '浏览j9平台最新资讯、使用教程和优惠活动信息',
  url: 'https://j9guanwang.com/category',
};

export default function CategoryPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [displayArticles, setDisplayArticles] = useState(articles);

  useEffect(() => {
    if (activeCategory === 'all') {
      setDisplayArticles(articles);
    } else if (activeCategory === 'platform') {
      setDisplayArticles(articles.filter((a) => a.category === '平台资讯'));
    } else if (activeCategory === 'tutorial') {
      setDisplayArticles(articles.filter((a) => a.category === '使用教程'));
    } else if (activeCategory === 'promotion') {
      setDisplayArticles(articles.filter((a) => a.category === '优惠活动'));
    }
  }, [activeCategory]);

  const handleCategoryChange = (catId: string) => {
    setActiveCategory(catId);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg schema={webpageSchema} />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-10 pb-8">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '平台资讯' },
              ]}
            />

            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mt-4 mb-3">
              平台<strong className="text-primary-500">资讯</strong>
            </h1>
            <p className="text-foreground-600 text-sm">
              了解j9平台最新动态、实用指南和超值优惠信息
            </p>
          </div>
        </div>

        <div className="sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-background-50 py-12 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {displayArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group block rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                          {article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                        <span>{article.date}</span>
                        <span>·</span>
                        <span>{article.readTime}阅读</span>
                      </div>
                      <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}